/**
 * ClientsPanel — API clients for an instance, managed by admins and owners.
 *
 * A client is the credential a server holds to call this instance: one name,
 * one scope, one secret. The secret is shown once, on create and on rotate,
 * with a copy control; the list never carries it. Session-cookie auth.
 */

import { useCallback, useEffect, useState } from "react";
import {
  listApiClients, createApiClient, rotateApiClientSecret, revokeApiClient,
  type ApiClient, type ApiClientSecret, type ClientScope,
} from "../api";
import { Pill } from "./ai/Primitives";
import { IconCheck, IconCopy } from "./icons";

export function ClientsPanel({ open, instance, onClose }: {
  open: boolean; instance: string; onClose: () => void;
}) {
  const [clients, setClients] = useState<ApiClient[]>([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const [name, setName] = useState("");
  const [scope, setScope] = useState<ClientScope>("mcp:read");
  const [busy, setBusy] = useState(false);
  const [issued, setIssued] = useState<ApiClientSecret | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      setClients((await listApiClients(instance)).clients);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [instance]);

  useEffect(() => { if (open) refresh(); }, [open, refresh]);

  if (!open) return null;

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null); setIssued(null);
    try {
      setIssued(await createApiClient(instance, name.trim(), scope));
      setName(""); setScope("mcp:read");
      await refresh();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };

  const rotate = async (c: ApiClient) => {
    if (!window.confirm(`Rotate the secret for ${c.client_name}? The current secret stops working.`)) return;
    setBusy(true); setErr(null); setIssued(null);
    try { setIssued(await rotateApiClientSecret(instance, c.client_id)); await refresh(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  const revoke = async (c: ApiClient) => {
    if (!window.confirm(`Revoke ${c.client_name}? Its secret stops working.`)) return;
    setBusy(true); setErr(null);
    try { await revokeApiClient(instance, c.client_id); if (issued?.client_id === c.client_id) setIssued(null); await refresh(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  };

  const live = clients.filter(c => !c.revoked_at);
  const revoked = clients.filter(c => c.revoked_at);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-wide" onClick={e => e.stopPropagation()}>
        <h2 className="modal-title">API clients — <code>{instance}</code></h2>

        <form onSubmit={create} style={S.form}>
          <input style={S.input} type="text" placeholder="Client name" value={name}
                 onChange={e => setName(e.target.value)} required maxLength={200} />
          <select style={S.select} value={scope} onChange={e => setScope(e.target.value as ClientScope)}>
            <option value="mcp:read">Read</option>
            <option value="mcp:read mcp:tools">Read + pipelines</option>
          </select>
          <button className="primary" type="submit" disabled={busy || !name.trim()}>
            {busy ? "Creating…" : "Create"}
          </button>
        </form>

        {issued && <Issued issued={issued} onDismiss={() => setIssued(null)} />}
        {err && <div className="error-banner">{err}</div>}

        {loading ? (
          <div style={S.muted}>Loading…</div>
        ) : (
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Name</th><th style={S.th}>Client ID</th><th style={S.th}>Scope</th>
                <th style={S.th}></th>
              </tr>
            </thead>
            <tbody>
              {live.map(c => (
                <tr key={c.client_id} title={c.created_by ? `Created by ${c.created_by}` : undefined}>
                  <td style={{ ...S.td, ...S.name }}>{c.client_name}</td>
                  <td style={S.td}><ClientId id={c.client_id} /></td>
                  <td style={S.td}>{scopePills(c.scope)}</td>
                  <td style={{ ...S.td, textAlign: "right", whiteSpace: "nowrap" }}>
                    <button style={S.rowBtn} disabled={busy} onClick={() => rotate(c)}>Rotate</button>
                    <button className="danger" style={S.rowBtn} disabled={busy} onClick={() => revoke(c)}>Revoke</button>
                  </td>
                </tr>
              ))}
              {revoked.map(c => (
                <tr key={c.client_id} style={{ opacity: 0.55 }} title={c.created_by ? `Created by ${c.created_by}` : undefined}>
                  <td style={{ ...S.td, ...S.name }}>{c.client_name}</td>
                  <td style={S.td}><ClientId id={c.client_id} /></td>
                  <td style={S.td}><Pill tone="neutral">Revoked</Pill></td>
                  <td style={S.td}></td>
                </tr>
              ))}
              {clients.length === 0 && (
                <tr><td style={{ ...S.td, color: "var(--ink-3)" }} colSpan={4}>No API clients yet.</td></tr>
              )}
            </tbody>
          </table>
        )}

        <div className="modal-actions"><button onClick={onClose}>Close</button></div>
      </div>
    </div>
  );
}

/** The one-time secret card: the bearer header, ready to copy. */
function Issued({ issued, onDismiss }: { issued: ApiClientSecret; onDismiss: () => void }) {
  return (
    <div style={S.card}>
      <div style={S.cardHead}>
        <span>{issued.client_name}</span>
        <Pill tone="running">Shown once</Pill>
      </div>
      <Row label="Bearer" value={issued.client_secret} mono />
      <Row label="Retrieve" value={issued.retrieve_endpoint} />
      <Row label="MCP" value={issued.mcp_endpoint} />
      <div style={{ textAlign: "right" }}><button style={S.rowBtn} onClick={onDismiss}>Done</button></div>
    </div>
  );
}

function Row({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { /* clipboard unavailable: the value is selectable */ }
  };
  return (
    <div style={S.row}>
      <span style={S.rowLabel}>{label}</span>
      <code style={{ ...S.rowValue, ...(mono ? S.secret : {}) }}>{value}</code>
      <button style={S.copyBtn} onClick={copy} title="Copy" aria-label={`Copy ${label}`}>
        {copied ? <IconCheck size={13} /> : <IconCopy size={13} />}
      </button>
    </div>
  );
}

/** The id, shortened; click copies the whole value. */
function ClientId({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(id); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { /* clipboard unavailable */ }
  };
  return (
    <button type="button" onClick={copy} title={id} style={S.idBtn}>
      <code style={S.code}>{id.slice(0, 12)}…</code>
      {copied ? <IconCheck size={12} /> : <IconCopy size={12} />}
    </button>
  );
}

function scopePills(scope: string) {
  const parts = scope.split(/\s+/).filter(Boolean);
  return (
    <span style={{ display: "inline-flex", gap: 4 }}>
      {parts.includes("mcp:read") && <Pill tone="neutral">Read</Pill>}
      {parts.includes("mcp:tools") && <Pill tone="accent">Pipelines</Pill>}
    </span>
  );
}

const S: Record<string, React.CSSProperties> = {
  form: { display: "flex", gap: 8, flexWrap: "wrap", margin: "8px 0 16px" },
  input: { flex: "1 1 200px" },
  select: { flex: "0 1 auto" },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 13 },
  th: { textAlign: "left", padding: "6px 8px", borderBottom: "1px solid var(--line-strong)", color: "var(--ink-3)", fontWeight: 600, fontSize: 12 },
  td: { padding: "9px 8px", borderBottom: "1px solid var(--line)", verticalAlign: "middle" },
  code: { fontSize: 12 },
  name: { fontWeight: 500, whiteSpace: "nowrap" },
  idBtn: { display: "inline-flex", alignItems: "center", gap: 5, padding: "2px 6px", fontSize: 12, background: "transparent", color: "var(--ink-3)" },
  muted: { color: "var(--ink-3)", fontSize: 13, padding: "12px 0" },
  rowBtn: { padding: "4px 10px", marginLeft: 6, fontSize: 12 },
  card: { border: "1px solid var(--line-strong)", borderRadius: 10, padding: "12px 14px", margin: "0 0 16px", background: "var(--bg-2, transparent)" },
  cardHead: { display: "flex", justifyContent: "space-between", alignItems: "center", fontWeight: 600, marginBottom: 8 },
  row: { display: "grid", gridTemplateColumns: "72px 1fr auto", alignItems: "center", gap: 8, padding: "4px 0" },
  rowLabel: { color: "var(--ink-3)", fontSize: 12 },
  rowValue: { fontSize: 12, overflowWrap: "anywhere", userSelect: "all" },
  secret: { fontWeight: 600 },
  copyBtn: { padding: "3px 6px", lineHeight: 0 },
};
